import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Item, Section } from '@react-stately/collections';
import type { Key, MouseEvent, ReactElement, ReactNode } from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useMedia } from 'react-use';
import type { Chain } from 'viem';

import { AvatarSkeleton, TextSkeleton } from '@/components/Skeleton';
import { SearchComboBoxItem } from '@/components/TransactionModule/TokenSelectModal/SearchComboBox/SearchComboBoxItem';
import styles from '@/components/TransactionModule/TokenSelectModal/styles.module.css';
import { TokenPreviewPopover } from '@/components/TransactionModule/TokenSelectModal/TokenPreviewPopover';
import type { PreviewToken } from '@/components/TransactionModule/TokenSelectModal/TokenPreviewPopover';
import { TokenSelectModalComboBox } from '@/components/TransactionModule/TokenSelectModal/TokenSelectModalComboBox';
import { getSearchComboBoxItemPropsForTokens } from '@/components/TransactionModule/TokenSelectModal/utils';
import type { SelectTokenFilter } from '@/components/TransactionModule/types';

import { solana } from '@/constants/chain';

import { dialogCloseBtnStyles, dialogStyles } from '@/styles/dialog';
import { fontStyles } from '@/styles/typography';
import { breakpoints } from '@/styles/util';

import { Overlay } from '@/ui/Dialog';
import { LoadingSpinner } from '@/ui/Icons/Animated/LoadingSpinner';
import { ChevronDownIcon } from '@/ui/Icons/ChevronDownIcon';
import { SearchIcon } from '@/ui/Icons/SearchIcon';
import { XIcon } from '@/ui/Icons/XIcon';

import type { TokenBase, TokenResult } from '@/utils/0x/token-registry.types';
import type { SEARCH_TYPES } from '@/utils/amplitude';
import { classNames } from '@/utils/classnames';
import { getTokenLogoURL } from '@/utils/url';

import { SelectTokenTrigger } from '../SelectToken';
import { loadMoreKey, tokenItemKey, type TokenSelectSection } from '../tokenSelectSections';
import { useTokenSelect } from '../useTokenSelect';

const loadingCells = new Array(8).fill(1);

type PreviewHandler = (token: PreviewToken | null) => void;

const LoadingMore = ({
  setPreviewTokenWithDelay,
}: {
  setPreviewTokenWithDelay: PreviewHandler;
}) => (
  <div className="items-center flex p-3 w-full" onMouseOver={() => setPreviewTokenWithDelay(null)}>
    <div className="mr-3 p-2">
      <LoadingSpinner variant="black" />
    </div>
    <span className={fontStyles({ variant: 'BODY_MD' })}>Loading more...</span>
  </div>
);

const ShowMore = ({ setPreviewTokenWithDelay }: { setPreviewTokenWithDelay: PreviewHandler }) => (
  <div className="items-center flex p-3 w-full" onMouseOver={() => setPreviewTokenWithDelay(null)}>
    <div className="mr-3 p-2">
      <ChevronDownIcon />
    </div>
    <span className={fontStyles({ variant: 'BODY_MD', class: ['text-token-gray-1600'] })}>
      Show more
    </span>
  </div>
);

function renderSections({
  sections,
  setPreviewTokenWithDelay,
  disabledReasons,
  onClearRecent,
}: {
  sections: TokenSelectSection[];
  setPreviewTokenWithDelay: PreviewHandler;
  disabledReasons: Map<string, ReactNode> | undefined;
  onClearRecent: (event: MouseEvent<HTMLButtonElement>) => void;
}) {
  return sections.map((section) => {
    const tokens = getSearchComboBoxItemPropsForTokens(section.tokens, setPreviewTokenWithDelay);
    const elements = section.isLoading
      ? loadingCells.map((_, index) => (
          <Item key={`loading-${index}-trendingTokens`} textValue="Show more">
            <div
              className="flex items-center gap-3 p-3"
              onMouseOver={() => setPreviewTokenWithDelay(null)}
            >
              <AvatarSkeleton className="relative" size={32}>
                <AvatarSkeleton className="absolute left-5 top-5" size={12} />
              </AvatarSkeleton>
              <div className="flex flex-col gap-1.5">
                <TextSkeleton width={212} height={14} />
                <TextSkeleton width={80} height={14} />
              </div>
            </div>
          </Item>
        ))
      : (tokens ?? []).map((token) => (
          <Item key={tokenItemKey(token, section.id)} textValue={token.name}>
            <SearchComboBoxItem
              {...token}
              logoUrl={
                token.logoUrl ??
                (token.chainId === solana.id
                  ? getTokenLogoURL(token.address, token.chainId)
                  : undefined)
              }
              disabledReason={disabledReasons?.get(`${token.chainId}-${token.address}`)}
            />
          </Item>
        ));

    if (section.hasMore) {
      elements.push(
        <Item key={loadMoreKey(section.id)} textValue="Show more">
          {section.isLoadingMore ? (
            <LoadingMore setPreviewTokenWithDelay={setPreviewTokenWithDelay} />
          ) : (
            <ShowMore setPreviewTokenWithDelay={setPreviewTokenWithDelay} />
          )}
        </Item>,
      );
    }

    return (
      <Section
        key={section.id}
        title={
          <div
            className="flex justify-between w-full"
            onMouseOver={() => setPreviewTokenWithDelay(null)}
          >
            {section.clearable ? <span>{section.title}</span> : section.title}
            {section.clearable && (
              <button
                className={fontStyles({
                  variant: 'BODY_SM',
                  class: ['bg-transparent', 'border-0', 'hover:text-token-gray-700'],
                })}
                onClick={onClearRecent}
                type="button"
              >
                Clear
              </button>
            )}
          </div>
        }
      >
        {elements}
      </Section>
    );
  });
}

type TokenSelectProps = {
  chainId?: number;
  onSelect: (token: TokenBase) => void;
  activeToken?: TokenResult;
  searchType: SEARCH_TYPES;
  supportedChains?: Chain[];
  isLoading?: boolean;
  filterToken?: SelectTokenFilter;
  children?: ReactElement;
  isHomepage?: boolean;
};

const TokenSelectComponent = ({
  chainId,
  onSelect,
  activeToken,
  searchType,
  supportedChains,
  isLoading,
  filterToken,
  children,
  isHomepage,
}: TokenSelectProps) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const handleSelect = useCallback(
    (token: TokenBase) => {
      onSelect(token);
      setOpen(false);
    },
    [onSelect],
  );

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        {children ?? (
          <SelectTokenTrigger
            activeToken={activeToken}
            searchType={searchType}
            isLoading={isLoading}
          />
        )}
      </Dialog.Trigger>
      <Dialog.Portal>
        <Overlay ref={overlayRef}>
          <TokenSelectContent
            chainId={chainId}
            supportedChains={supportedChains}
            searchType={searchType}
            isOpen={open}
            filterToken={filterToken}
            onSelect={handleSelect}
            isHomepage={isHomepage}
          />
        </Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

/**
 * Opens the token picker from the standard trigger or supplied children.
 *
 * Memoized so frequently re-rendering panels can skip rebuilding the dialog subtree.
 */
export const TokenSelect = memo(TokenSelectComponent);

function TokenSelectContent({
  chainId,
  supportedChains,
  searchType,
  isOpen,
  filterToken,
  onSelect,
  isHomepage,
}: {
  chainId?: number;
  supportedChains?: Chain[];
  searchType: SEARCH_TYPES;
  isOpen: boolean;
  filterToken?: SelectTokenFilter;
  onSelect: (token: TokenBase) => void;
  isHomepage?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewToken, setPreviewToken] = useState<PreviewToken | null>(null);
  const [shouldShowPreview, setShouldShowPreview] = useState(false);
  const previewTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [focusedKey, setFocusedKey] = useState<Key | null>(null);
  const hasUserInteractedRef = useRef(false);
  const previousFocusedKeyRef = useRef<Key | null>(null);
  const isDesktop = useMedia(breakpoints.desktop, false);
  const {
    inputValue,
    onInputChange,
    selectedChainId,
    onChangeChainFilter,
    suggestedTokensChainId,
    sections,
    disabledKeys,
    disabledReasons,
    resolveItem,
    handleSelectionChange,
    selectTokenResult,
    clearRecentlyViewed,
    isSearching,
    isLoadingTrending,
    hasError,
    noResults,
  } = useTokenSelect({
    chainId,
    supportedChains,
    isOpen,
    searchType,
    filterToken,
    onSelect,
  });

  useEffect(() => {
    if (isOpen) {
      hasUserInteractedRef.current = false;
      previousFocusedKeyRef.current = null;
    }
  }, [isOpen]);

  const setPreviewTokenWithDelay = useCallback((token: PreviewToken | null) => {
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = null;
    }
    if (token) {
      previewTimeoutRef.current = setTimeout(() => {
        setPreviewToken(token);
        setShouldShowPreview(true);
        previewTimeoutRef.current = null;
      }, 400);
    } else {
      setShouldShowPreview(false);
    }
  }, []);

  const handleExitComplete = useCallback(() => setPreviewToken(null), []);

  useEffect(
    () => () => {
      clearTimeout(previewTimeoutRef.current ?? undefined);
    },
    [],
  );

  const handleKeyboardFocusChange = useCallback(
    (key: Key | null) => {
      if (key === null) {
        setFocusedKey(null);
        return;
      }
      const item = resolveItem(key);
      if (!item) return;
      if (item.kind === 'loadMore') {
        setFocusedKey(null);
        previousFocusedKeyRef.current = null;
        return;
      }

      if (previousFocusedKeyRef.current !== null && previousFocusedKeyRef.current !== key) {
        hasUserInteractedRef.current = true;
      }
      setFocusedKey(key);
      previousFocusedKeyRef.current = key;
    },
    [resolveItem],
  );

  useEffect(() => {
    if (focusedKey && hasUserInteractedRef.current) {
      const item = resolveItem(focusedKey);
      if (item?.kind === 'token') {
        const { token } = item;
        setPreviewTokenWithDelay({
          address: token.address,
          chainId: token.chainId,
          logoUrl: token.logo ?? undefined,
          name: token.name,
          symbol: token.symbol,
        });
      }
    } else if (!focusedKey) {
      setPreviewTokenWithDelay(null);
    }
  }, [focusedKey, resolveItem, setPreviewTokenWithDelay]);

  const handleInputChange = useCallback(
    (value: string) => {
      setPreviewTokenWithDelay(null);
      onInputChange(value);
    },
    [onInputChange, setPreviewTokenWithDelay],
  );
  const handleClearRecentlyViewed = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      inputRef.current?.focus();
      clearRecentlyViewed();
      event.stopPropagation();
    },
    [clearRecentlyViewed],
  );

  return (
    <Dialog.Content
      className={classNames(
        'h-full',
        'm-auto',
        'max-h-full',
        'w-full',
        'tablet:w-auto',
        'tablet:h-[730px]',
        'relative',
        styles.animatedDialog,
        isHomepage ? styles.animatedDialogSafariHomepage : '',
      )}
      id="token-select"
      forceMount
      onOpenAutoFocus={(event) => {
        event.preventDefault();
        inputRef.current?.focus();
      }}
      onCloseAutoFocus={(event) => event.preventDefault()}
      aria-describedby={undefined}
    >
      <div className="rounded-[1.25rem] shadow-[0px_8px_36px_rgba(55,65,81,0.15)]">
        <div className={dialogStyles({ size: 'large', class: ['z-100'] })}>
          <VisuallyHidden>
            <Dialog.Title className={fontStyles({ variant: 'H5' })}>Search tokens</Dialog.Title>
          </VisuallyHidden>

          <Dialog.Close className={dialogCloseBtnStyles().button({ absolute: true })}>
            <XIcon aria-hidden className={dialogCloseBtnStyles().icon()} />
          </Dialog.Close>

          <div className="absolute p-0.5 left-4.5 top-4.5 z-10">
            <SearchIcon className="h-5 w-5" />
          </div>

          <TokenSelectModalComboBox
            chainId={selectedChainId}
            disabledKeys={disabledKeys}
            filterToken={filterToken}
            inputValue={inputValue}
            isLoading={isLoadingTrending}
            isSearching={isSearching}
            error={hasError}
            label="Search for a token"
            menuTrigger="focus"
            noResults={noResults}
            onChangeChainFilter={onChangeChainFilter}
            onInputChange={handleInputChange}
            onKeyboardFocusChange={handleKeyboardFocusChange}
            onSelectionChange={handleSelectionChange}
            suggestedTokensChainId={suggestedTokensChainId}
            onSelectTokenResult={selectTokenResult}
            placeholder="Search token name or paste address"
            ref={inputRef}
            selectedKey={null}
            supportedChains={supportedChains}
            setPreviewTokenWithDelay={setPreviewTokenWithDelay}
          >
            {renderSections({
              sections,
              setPreviewTokenWithDelay,
              disabledReasons,
              onClearRecent: handleClearRecentlyViewed,
            })}
          </TokenSelectModalComboBox>
        </div>
      </div>
      {isDesktop && previewToken && (
        <TokenPreviewPopover
          {...previewToken}
          isVisible={shouldShowPreview}
          onExitComplete={handleExitComplete}
        />
      )}
    </Dialog.Content>
  );
}
